/**
 * Server settings › Emoji (2026-11-01), as Discord's Server Settings ›
 * Emoji: the upload requirements, Upload emoji, then the still and the
 * animated emoji, each list with its free slots (50 each). A row is the
 * image, its name (renamed in place: Enter or leaving the field saves,
 * Escape puts it back), who uploaded it, and Delete.
 *
 * - Create Expressions uploads, and renames or deletes the emoji it added;
 *   Manage Expressions renames or deletes any (the server checks again).
 * - An image of any size is shrunk in the browser to fit 128 x 128 and
 *   256 KB (lib/image, purpose `emoji`; a small animated GIF or WebP goes
 *   as it is and stays animated), then the server re-encodes it.
 * - The name starts as the file's (`emojiNameFrom`), made unique.
 * - The console's `custom_emoji` flag off: only deleting is left.
 * - Phones: each row wraps under its image; the lists are the page's own.
 */
import {
  EMOJI_NAME_TAKEN,
  EMOJI_UPLOAD_TYPES,
  emojiNameFrom,
  isEmojiName,
  MAX_ANIMATED_EMOJIS,
  MAX_STATIC_EMOJIS,
  Permissions,
  type ComputedPermissions,
  type GuildEmoji,
  type Snowflake,
  type User,
} from '@neosocial/protocol';
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useT } from '../i18n';
import { isApiRequestError } from '../lib/api';
import { blobToDataUri, imageErrorText, isAbort, optimizeImage, stillText } from '../lib/image';
import { runAction } from '../mod/actions';
import { flagOn } from '../store/config';
import { useData, useDataShallow } from '../store/data';
import { emojiApi, setGuildEmojis, useCustomEmojiOn, useEmoji } from '../store/emoji';
import { onGatewayEvent } from '../store/umEvents';
import { basePermissions, can, displayName, useActiveGuildId } from '../store/selectors';
import { Avatar, Btn, IconBtn } from '../ui/kit';
import { confirmDialog, toast } from '../ui/overlay';
import { customEmojiUrl } from '../hub/customEmoji';

const EMPTY: GuildEmoji[] = [];

/** Whether the signed-in user gets the Emoji tab: Create or Manage Expressions. */
export function canSeeEmojiTab(base: ComputedPermissions): boolean {
  return (
    can(base, Permissions.CREATE_GUILD_EXPRESSIONS) ||
    can(base, Permissions.MANAGE_GUILD_EXPRESSIONS)
  );
}

/** A name from a file name that no emoji of the list has yet (`party`, `party_2`...). */
export function freeEmojiName(file: string, taken: ReadonlySet<string>): string {
  const base = emojiNameFrom(file);
  if (!taken.has(base)) return base;
  for (let i = 2; i < 1000; i++) {
    const tail = `_${i}`;
    const n = base.slice(0, 32 - tail.length) + tail;
    if (!taken.has(n)) return n;
  }
  return base;
}

export function EmojiTab() {
  const { t } = useT();
  const gid = useActiveGuildId();
  const me = useData((d) => d.me?.id ?? null);
  const base = useDataShallow((d) => basePermissions(d, gid));
  const list = useEmoji((s) => (gid ? (s.byGuild[gid] ?? EMPTY) : EMPTY));
  const on = useCustomEmojiOn();
  const canCreate = can(base, Permissions.CREATE_GUILD_EXPRESSIONS);
  const canManage = can(base, Permissions.MANAGE_GUILD_EXPRESSIONS);
  const [busy, setBusy] = useState(false);
  // who uploaded each (GET /guilds/{id}/emojis gives it to Create and Manage Expressions)
  const [uploaders, setUploaders] = useState<Record<Snowflake, User>>({});
  const file = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!gid) return;
    const load = () =>
      void emojiApi
        .list(gid)
        .then((l) => {
          const by: Record<Snowflake, User> = {};
          for (const e of l) if (e.user) by[e.id] = e.user;
          setUploaders(by);
        })
        .catch(() => {});
    load();
    return onGatewayEvent((f) => {
      if (f.t === 'GUILD_EMOJIS_UPDATE' && f.d.guild_id === gid) load();
    });
  }, [gid]);

  if (!gid) return null;
  const still = list.filter((e) => !e.animated);
  const animated = list.filter((e) => e.animated);
  const full = still.length >= MAX_STATIC_EMOJIS && animated.length >= MAX_ANIMATED_EMOJIS;
  const mayManage = (e: GuildEmoji) => canManage || (canCreate && uploaders[e.id]?.id === me);

  const upload = async (files: File[]) => {
    if (!flagOn('image_uploads')) {
      toast(t('cfg.off.image_uploads'), { icon: 'image', tone: 'warn' });
      return;
    }
    setBusy(true);
    try {
      for (const f of files) {
        if (!EMOJI_UPLOAD_TYPES.includes(f.type)) {
          toast(t('emo.err.type'), { icon: 'image', tone: 'warn' });
          continue;
        }
        let image: string;
        try {
          const r = await optimizeImage(f, 'emoji');
          if (r.still) toast(stillText(t, r.still), { icon: 'image' });
          image = await blobToDataUri(r.blob);
        } catch (e) {
          if (!isAbort(e)) toast(imageErrorText(t, e), { icon: 'image', tone: 'warn' });
          continue;
        }
        const taken = new Set(useEmoji.getState().byGuild[gid]?.map((x) => x.name) ?? []);
        const name = freeEmojiName(f.name || 'emoji', taken);
        const r = await runAction(() => emojiApi.create(gid, { name, image }));
        if (!r.ok) break;
        // the gateway's GUILD_EMOJIS_UPDATE says the same; this one is at once
        const now = useEmoji.getState().byGuild[gid] ?? [];
        if (!now.some((x) => x.id === r.value.id)) setGuildEmojis(gid, [...now, r.value]);
        if (r.value.user) setUploaders((u) => ({ ...u, [r.value.id]: r.value.user! }));
        toast(t('emo.uploaded', { name: r.value.name }), { icon: 'check', tone: 'ok' });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ns-tool ns-emoji" data-emoji-tab="">
      <p className="ns-muted">{t('emo.intro')}</p>
      {!on ? (
        <p className="ns-emo-off" role="status">
          {t('emo.off')}
        </p>
      ) : null}
      <div className="ns-emo-req">
        <div className="ns-emo-req-t">
          <b>{t('emo.req.title')}</b>
          <ul>
            <li>{t('emo.req.type')}</li>
            <li>{t('emo.req.size')}</li>
            <li>{t('emo.req.anim')}</li>
            <li>{t('emo.req.name')}</li>
          </ul>
        </div>
        {canCreate ? (
          <div className="ns-emo-up">
            <Btn
              kind="primary"
              size="sm"
              icon="upload"
              busy={busy}
              disabled={!on || full || busy}
              onClick={() => file.current?.click()}
            >
              {busy ? t('emo.uploading') : t('emo.upload')}
            </Btn>
            {full ? <small className="ns-muted">{t('emo.full')}</small> : null}
            <input
              ref={file}
              type="file"
              accept={EMOJI_UPLOAD_TYPES.join(',')}
              multiple
              hidden
              data-emoji-file=""
              onChange={(e) => {
                const fs = Array.from(e.target.files ?? []);
                e.target.value = '';
                if (fs.length) void upload(fs);
              }}
            />
          </div>
        ) : null}
      </div>
      <EmojiList
        title={t('emo.still')}
        list={still}
        max={MAX_STATIC_EMOJIS}
        gid={gid}
        on={on}
        uploaders={uploaders}
        mayManage={mayManage}
      />
      <EmojiList
        title={t('emo.animated')}
        list={animated}
        max={MAX_ANIMATED_EMOJIS}
        gid={gid}
        on={on}
        uploaders={uploaders}
        mayManage={mayManage}
      />
    </div>
  );
}

function EmojiList({
  title,
  list,
  max,
  gid,
  on,
  uploaders,
  mayManage,
}: {
  title: string;
  list: GuildEmoji[];
  max: number;
  gid: Snowflake;
  on: boolean;
  uploaders: Record<Snowflake, User>;
  mayManage(e: GuildEmoji): boolean;
}) {
  const { t, fmt } = useT();
  return (
    <section className="ns-emo-sec" aria-label={title}>
      <h3 className="ns-emo-h">
        <span>{title}</span>
        <small className="nx-num">
          {t('emo.slots', { n: fmt.num(Math.max(0, max - list.length)), max: fmt.num(max) })}
        </small>
      </h3>
      {list.length ? (
        <ul className="ns-tool-list ns-emo-list">
          {list.map((e) => (
            <EmojiRow
              key={e.id}
              e={e}
              gid={gid}
              on={on}
              by={uploaders[e.id] ?? null}
              manage={mayManage(e)}
            />
          ))}
        </ul>
      ) : (
        <p className="ns-muted">{t('emo.empty')}</p>
      )}
    </section>
  );
}

function EmojiRow({
  e,
  gid,
  on,
  by,
  manage,
}: {
  e: GuildEmoji;
  gid: Snowflake;
  on: boolean;
  by: User | null;
  manage: boolean;
}) {
  const { t } = useT();
  const [draft, setDraft] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const value = draft ?? e.name;
  const cancel = () => {
    setDraft(null);
    setErr(null);
  };
  const save = async () => {
    if (draft === null || draft === e.name) return cancel();
    if (!isEmojiName(draft)) {
      setErr(t('emo.err.name'));
      return;
    }
    if (useEmoji.getState().byGuild[gid]?.some((x) => x.id !== e.id && x.name === draft)) {
      setErr(t('emo.err.taken'));
      return;
    }
    setSaving(true);
    const r = await runAction(() => emojiApi.rename(gid, e.id, { name: draft }), { quiet: true });
    setSaving(false);
    if (!r.ok) {
      const taken =
        isApiRequestError(r.error) &&
        r.error.body?.errors?.name?.some((x) => x.code === EMOJI_NAME_TAKEN);
      setErr(taken ? t('emo.err.taken') : t('emo.err.name'));
      return;
    }
    const now = useEmoji.getState().byGuild[gid] ?? [];
    setGuildEmojis(
      gid,
      now.map((x) => (x.id === e.id ? { ...x, name: r.value.name } : x)),
    );
    cancel();
    toast(t('emo.renamed', { name: r.value.name }), { icon: 'check', tone: 'ok' });
  };
  const remove = async () => {
    const ok = await confirmDialog({
      title: t('emo.deleteTitle', { name: e.name }),
      body: t('emo.deleteBody'),
      confirmLabel: t('emo.deleteOk'),
      danger: true,
      icon: 'trash',
    });
    if (!ok) return;
    const r = await runAction(() => emojiApi.remove(gid, e.id));
    if (!r.ok) return;
    const now = useEmoji.getState().byGuild[gid] ?? [];
    setGuildEmojis(
      gid,
      now.filter((x) => x.id !== e.id),
    );
    toast(t('emo.deleted', { name: e.name }), { icon: 'check', tone: 'ok' });
  };
  const onKey = (ev: KeyboardEvent<HTMLInputElement>) => {
    if (ev.key === 'Enter') {
      ev.preventDefault();
      void save();
    } else if (ev.key === 'Escape') {
      ev.preventDefault();
      ev.stopPropagation();
      cancel();
    }
  };
  return (
    <li className="ns-tool-row ns-emo-row" data-emoji-id={e.id}>
      <span className="ns-emo-img">
        <img src={customEmojiUrl(e)} alt={`:${e.name}:`} loading="lazy" decoding="async" />
      </span>
      <span className="ns-emo-name">
        <span className="ns-emo-colon" aria-hidden="true">
          :
        </span>
        <input
          className="nx-input"
          dir="ltr"
          value={value}
          maxLength={32}
          spellCheck={false}
          autoCapitalize="off"
          autoCorrect="off"
          aria-label={t('emo.nameOf', { name: e.name })}
          aria-invalid={err ? true : undefined}
          disabled={!manage || !on || saving}
          onChange={(ev) => {
            setErr(null);
            // as Discord: lower case, a space or a dash becomes _
            setDraft(ev.target.value.toLowerCase().replace(/[\s-]/g, '_'));
          }}
          onKeyDown={onKey}
          onBlur={() => void save()}
        />
        <span className="ns-emo-colon" aria-hidden="true">
          :
        </span>
        {err ? (
          <small className="ns-err" role="alert">
            {err}
          </small>
        ) : null}
      </span>
      <span className="ns-emo-by">
        {by ? (
          <>
            <Avatar user={by} name={displayName(by)} size={20} />
            <bdi>{displayName(by)}</bdi>
          </>
        ) : null}
      </span>
      {manage ? (
        <IconBtn
          icon="x"
          size="xs"
          label={t('emo.delete', { name: e.name })}
          className="ns-emo-del"
          onClick={() => void remove()}
        />
      ) : null}
    </li>
  );
}
